"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
export default function CommonQuestions() {
  const {
    ref: preambleRef,
    isVisible: preambleVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: accordionRef,
    isVisible: accordionVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const protocols = [{
    number: "01",
    question: "How does your SEO process work?",
    paragraphs: ["We begin with a comprehensive audit of your current digital footprint. Our team identifies technical bottlenecks, content gaps, and backlink opportunities to build a roadmap for sustainable growth.", "Our strategy is data-driven, focusing on high-intent keywords that connect your business with customers actively searching for your services."]
  }, {
    number: "02",
    question: "Can you fix my Google Business Profile?",
    paragraphs: ["Absolutely. We specialize in resolving suspension issues, optimizing listing information, and managing review responses to ensure your business appears prominently in local map results.", "We ensure your NAP (Name, Address, Phone) consistency across the web, which is a critical factor for local search ranking."]
  }, {
    number: "03",
    question: "What makes your web design different?",
    paragraphs: ["We build websites that are not just visually stunning but engineered for conversion. Every design decision is based on user experience principles to guide visitors toward taking action.", "Our sites are fully responsive, lightning-fast, and optimized for search engines from the ground up, ensuring you rank well while looking professional."]
  }, {
    number: "04",
    question: "How long until I see results?",
    paragraphs: ["SEO is a long-term investment. While technical fixes can show immediate improvements in site health, organic ranking growth typically becomes noticeable within three to six months.", "We provide transparent monthly reporting so you can track progress, keyword movement, and the return on your investment."]
  }, {
    number: "05",
    question: "Do you work with small businesses?",
    paragraphs: ["Yes, small to medium-sized businesses are our primary focus. We understand the unique challenges you face and provide scalable solutions that fit your budget and growth goals.", "Whether you are a local service provider or a growing e-commerce brand, we tailor our approach to your specific market needs."]
  }, {
    number: "06",
    question: "What is included in local SEO?",
    paragraphs: ["Our local SEO package includes Google Business Profile management, local citation building, geo-targeted keyword optimization, and reputation management.", "We focus on getting you into the 'Local Pack'—the top three map results that drive the majority of local traffic and phone calls."]
  }, {
    number: "07",
    question: "How do we get started?",
    paragraphs: ["Getting started is simple. Schedule a discovery call through our contact page, and we will review your current online presence and discuss how Asahi Search can help you grow.", "We welcome all questions and are happy to provide a preliminary assessment of your website or Google listing during our first conversation."]
  }];
  return <section data-section-id="1615" id="common-questions" className="relative w-full bg-background overflow-hidden !p-0">
      <Separator className="absolute top-0 left-0 right-0 bg-border" />

      <div className="mx-auto max-w-[1440px] px-6 md:px-16 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-8">
          {}
          <div ref={preambleRef} className="lg:col-span-5 relative">
            <div className="lg:sticky lg:top-32">
              <div className="relative">
                {}
                <span aria-hidden="true" className="pointer-events-none absolute -right-4 -top-12 md:-top-20 font-default font-thin leading-none select-none text-border/30" style={{
                fontSize: "clamp(10rem, 22vw, 22rem)",
                fontWeight: 100
              }}>
                  07
                </span>

                {}
                <div className={`relative font-mono uppercase text-primary transition-all duration-700 ease-out ${preambleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                fontSize: "clamp(0.6875rem, 0.78vw, 0.8125rem)",
                letterSpacing: "0.18em"
              }}>
                  N° 07 — COMMON QUESTIONS
                </div>

                {}
                <h2 className={`relative font-default text-foreground mt-12 md:mt-14 transition-all duration-700 ease-out delay-[80ms] ${preambleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                fontSize: "clamp(2.25rem, 4.8vw, 4.75rem)",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                lineHeight: 1.02
              }}>
                  Answers to your <em className="italic font-light">growth</em> questions.
                </h2>

                {}
                <p className={`relative font-default text-muted-foreground mt-10 max-w-md transition-all duration-700 ease-out delay-[160ms] ${preambleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                fontSize: "clamp(1.0625rem, 1.15vw, 1.25rem)",
                fontWeight: 400,
                letterSpacing: "-0.005em",
                lineHeight: 1.55
              }}>
                  We believe in total transparency. Here are the most common inquiries we receive from business owners looking to scale their digital presence with Asahi Search.
                </p>

                {}
                <div className={`relative mt-12 font-mono uppercase text-muted-foreground transition-all duration-700 ease-out delay-[240ms] ${preambleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                fontSize: "clamp(0.6875rem, 0.75vw, 0.75rem)",
                letterSpacing: "0.08em"
              }}>
                  UPDATED · OCT 2024
                </div>
              </div>
            </div>
          </div>

          {}
          <div ref={accordionRef} className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full border-t border-border">
              {protocols.map((item, i) => <AccordionItem data-index={i} key={item.number} value={`item-${item.number}`} className={`border-b border-border last:border-b-0 transition-all duration-700 ease-out ${accordionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
              transitionDelay: `${i * 60}ms`
            }}>
                  <AccordionTrigger className="group py-8 hover:no-underline rounded-lg data-[state=open]:[&_.indicator]:text-primary">
                    <div className="flex-1 grid grid-cols-12 items-baseline gap-4 pr-4">
                      <div className="col-span-9 md:col-span-9 text-left">
                        <h4 className="font-default text-foreground" style={{
                      fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
                      fontWeight: 600,
                      letterSpacing: "0em",
                      lineHeight: 1.3
                    }}>
                          {item.question}
                        </h4>
                      </div>
                      <div className="hidden md:block md:col-span-2 font-mono text-muted-foreground" style={{
                    fontSize: "clamp(0.6875rem, 0.75vw, 0.75rem)",
                    letterSpacing: "0.08em"
                  }}>
                        {item.number}
                      </div>
                      <div className="col-span-3 md:col-span-1 text-right font-mono text-foreground indicator transition-colors duration-300" style={{
                    fontSize: "clamp(0.875rem, 1vw, 1rem)",
                    letterSpacing: "0.05em"
                  }}>
                        <span className="group-data-[state=open]:hidden">+</span>
                        <span className="hidden group-data-[state=open]:inline">
                          —
                        </span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-0 pb-0">
                    <div className="bg-muted/30 py-10 px-0">
                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 md:col-span-10 md:col-start-1 space-y-5">
                          {item.paragraphs.map((p, pi) => <p data-index={pi} key={pi} className="font-default text-muted-foreground" style={{
                        fontSize: "clamp(0.9375rem, 1vw, 1rem)",
                        fontWeight: 400,
                        lineHeight: 1.65
                      }}>
                              {p}
                            </p>)}

                          <Separator className="bg-border mt-8" />

                          <Link to="/ContactUsPage" className="inline-block mt-2 font-mono uppercase text-primary hover:text-primary/80 transition-colors duration-220" style={{
                        fontSize: "clamp(0.6875rem, 0.75vw, 0.75rem)",
                        letterSpacing: "0.08em",
                        textDecoration: "underline",
                        textUnderlineOffset: "6px"
                      }}>
                            Contact Our Team — Asahi Search
                          </Link>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>

      <Separator className="absolute bottom-0 left-0 right-0 bg-border" />
    </section>;
}